<template>
  <v-container class="external-changes-protect">
    <v-row justify="center">
      <ViewHeader :title="$t('message.externalChanges.protect.title')" />
      <v-col cols="12">
        <v-card class="external-changes-protect__card">
          <v-container v-if="loaded">
            <v-row>
              <v-col>
                <p>{{ $t("message.externalChanges.protect.description") }}</p>

                <ExternalChangesInfoSection v-if="showInfoSection" />

                <div class="fields-table__headings">
                  <div class="col col--name"></div>
                  <div class="col col--external flex-column">
                    <h4>
                      {{ $t("message.externalChanges.protect.type.external") }}
                    </h4>
                    <div class="fields-table__labels">
                      <p>
                        {{ $t("message.externalChanges.protect.alwaysReject") }}
                      </p>
                    </div>
                  </div>
                  <div class="col col--gmb flex-column">
                    <h4>
                      {{ $t("message.externalChanges.protect.type.gmb") }}
                    </h4>
                    <div class="fields-table__labels">
                      <p>
                        {{ $t("message.externalChanges.protect.alwaysAccept") }}
                      </p>
                      <p>
                        {{ $t("message.externalChanges.protect.alwaysReject") }}
                      </p>
                    </div>
                  </div>
                </div>
                <v-card class="fields-table">
                  <div class="field field--all">
                    <div class="col col--name">
                      {{ $t("message.externalChanges.protect.allFields") }}
                    </div>
                    <div class="col col--external">
                      <div
                        v-for="checkbox in checkboxesExternal"
                        class="field__checkbox-wrapper"
                        :key="`all-${checkbox.id}`"
                      >
                        <v-checkbox
                          :inputValue="
                            areAllChecked(checkbox.source, checkbox.status)
                          "
                          @change="
                            handleCheckAll(
                              $event,
                              checkbox.source,
                              checkbox.status
                            )
                          "
                        />
                      </div>
                    </div>
                    <div class="col col--gmb">
                      <div
                        v-for="checkbox in checkboxesGmb"
                        class="field__checkbox-wrapper"
                        :key="`all-${checkbox.id}`"
                      >
                        <v-checkbox
                          :inputValue="
                            areAllChecked(checkbox.source, checkbox.status)
                          "
                          @change="
                            handleCheckAll(
                              $event,
                              checkbox.source,
                              checkbox.status
                            )
                          "
                        />
                      </div>
                    </div>
                  </div>
                  <div v-for="field in fieldsArr" :key="field.id" class="field">
                    <div class="col col--name">
                      {{ $t(`message.externalChanges.fields.${field.name}`) }}

                      <v-tooltip
                        v-if="
                          $te(
                            `message.externalChanges.fieldsTooltips.${field.name}`
                          )
                        "
                        right
                        :max-width="300"
                      >
                        <template v-slot:activator="{ on }">
                          <div class="info-icon" v-on="on">
                            <v-icon color="lime" size="19"
                              >mdi-information</v-icon
                            >
                          </div>
                        </template>
                        <span>
                          {{
                            $t(
                              `message.externalChanges.fieldsTooltips.${field.name}`
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </div>
                    <div class="col col--external">
                      <div
                        v-for="checkbox in checkboxesExternal"
                        class="field__checkbox-wrapper"
                        :key="checkbox.id"
                      >
                        <v-checkbox
                          :class="{
                            'd-none':
                              (field.name ===
                                EXTERNAL_CHANGES_FIELD.LOCATION_STATE &&
                                checkbox.status ===
                                  EXTERNAL_CHANGES_PROTECT_STATUS.REJECT) ||
                              field.name === EXTERNAL_CHANGES_FIELD.LABELS
                          }"
                          :inputValue="
                            isChecked(field, checkbox.source, checkbox.status)
                          "
                          @change="
                            handleCheck(
                              $event,
                              field.name,
                              checkbox.source,
                              checkbox.status
                            )
                          "
                        />
                      </div>
                    </div>
                    <div class="col col--gmb">
                      <div
                        v-for="checkbox in checkboxesGmb"
                        class="field__checkbox-wrapper"
                        :key="checkbox.id"
                      >
                        <v-checkbox
                          :class="{
                            'd-none':
                              field.name ===
                                EXTERNAL_CHANGES_FIELD.LOCATION_STATE &&
                              checkbox.status ===
                                EXTERNAL_CHANGES_PROTECT_STATUS.REJECT
                          }"
                          :disabled="
                            field.name === EXTERNAL_CHANGES_FIELD.LOCATION_STATE
                          "
                          :inputValue="
                            isChecked(field, checkbox.source, checkbox.status)
                          "
                          @change="
                            handleCheck(
                              $event,
                              field.name,
                              checkbox.source,
                              checkbox.status
                            )
                          "
                        />
                      </div>
                    </div>
                  </div>
                </v-card>
                <div class="mt-4 d-flex justify-end">
                  <ui-button color="primary" @click="handleSave">
                    {{ $t("message.common.save") }}
                  </ui-button>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <SectionLoader :status="loading.view" />
        </v-card>
      </v-col>
    </v-row>
    <ProtectInfoDialog
      :is-open="infoDialogVisible"
      @close="infoDialogVisible = false"
    />
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import ProtectInfoDialog from "@/components/ExternalChanges/dialog/ProtectInfoDialog.vue";
import ExternalChangesInfoSection from "@/components/ExternalChanges/ExternalChangesInfoSection";

import { mapGetters, mapState } from "vuex";
import cuid from "cuid";

import ErrorHandler from "@/helpers/handlers/error";
import gmbExternalChangesService from "@/services/externalChanges";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import {
  EXTERNAL_CHANGES_FIELD,
  EXTERNAL_CHANGES_PROTECT_SOURCE,
  EXTERNAL_CHANGES_PROTECT_STATUS
} from "@/enums/external-changes";
import { gtmEvent__utmEmailNotification } from "@/helpers/gtm-events";
import { setCompanyIdFromQuery as setCompanyIdFromQueryMixin } from "@/helpers/mixins/set-company-id-from-query";
import { isUtmTaggedUrl } from "@/helpers/utm-tags";

export default {
  name: "ExternalChangesProtect",
  components: {
    ViewHeader,
    SectionLoader,
    ProtectInfoDialog,
    ExternalChangesInfoSection
  },
  mixins: [setCompanyIdFromQueryMixin],
  data() {
    return {
      EXTERNAL_CHANGES_FIELD,
      EXTERNAL_CHANGES_PROTECT_SOURCE,
      EXTERNAL_CHANGES_PROTECT_STATUS,
      loaded: false,
      loading: {
        view: false
      },
      id: null,
      fieldsArr: [],
      checkboxes: [
        {
          source: EXTERNAL_CHANGES_PROTECT_SOURCE.EXTERNAL,
          status: EXTERNAL_CHANGES_PROTECT_STATUS.REJECT,
          id: cuid()
        },
        {
          source: EXTERNAL_CHANGES_PROTECT_SOURCE.GMB,
          status: EXTERNAL_CHANGES_PROTECT_STATUS.ACCEPT,
          id: cuid()
        },
        {
          source: EXTERNAL_CHANGES_PROTECT_SOURCE.GMB,
          status: EXTERNAL_CHANGES_PROTECT_STATUS.REJECT,
          id: cuid()
        }
      ],
      infoDialogVisible: false
    };
  },

  computed: {
    checkboxesExternal() {
      return this.checkboxes.filter(
        checkbox => checkbox.source === EXTERNAL_CHANGES_PROTECT_SOURCE.EXTERNAL
      );
    },
    checkboxesGmb() {
      return this.checkboxes.filter(
        checkbox => checkbox.source === EXTERNAL_CHANGES_PROTECT_SOURCE.GMB
      );
    },
    ...mapState({
      company: state => state.company.company
    }),
    showInfoSection() {
      return !this.company.enabled_auto_gmb_external_changes_protection;
    }
  },

  async created() {
    this.loading.view = true;
    await this.setFields();
    this.loaded = true;
    this.loading.view = false;

    if (isUtmTaggedUrl(this.$route.query)) {
      gtmEvent__utmEmailNotification(this.$route.query);
    }
  },

  methods: {
    async setFields() {
      const result = await gmbExternalChangesService.fetchExternalChangesSettings();

      if (result && result.data && result.data[0]) {
        const data = result.data[0];

        this.id = data.id;
        this.fieldsArr = Object.keys(data)
          .filter(key => Object.values(EXTERNAL_CHANGES_FIELD).includes(key))
          .map(key => {
            return {
              id: cuid(),
              name: key,
              status: data[key]
            };
          });
      }
    },

    areAllChecked(source, status) {
      let fields = [...this.fieldsArr];
      if (status === EXTERNAL_CHANGES_PROTECT_STATUS.REJECT) {
        // as "location_state" cannot be set to "reject", for check, we need to omit "location_state" field
        fields = fields.filter(
          field => field.name !== EXTERNAL_CHANGES_FIELD.LOCATION_STATE
        );
      }
      if (source === EXTERNAL_CHANGES_PROTECT_SOURCE.EXTERNAL) {
        // as "labels" are not supported from external source, for check, we need to omit "labels" field
        fields = fields.filter(
          field => field.name !== EXTERNAL_CHANGES_FIELD.LABELS
        );
      }

      return fields.every(field => field["status"][source] === status);
    },

    isChecked(field, source, status) {
      return field.status[source] === status;
    },

    handleCheckAll(state, source, status) {
      if (state) {
        this.fieldsArr.forEach(field => {
          if (
            // as "location_state" cannot be set to "reject", uncheck "always accept" on "reject" all action
            (field.name === EXTERNAL_CHANGES_FIELD.LOCATION_STATE &&
              status === EXTERNAL_CHANGES_PROTECT_STATUS.REJECT) ||
            // "labels" are not supported from external source
            (field.name === EXTERNAL_CHANGES_FIELD.LABELS &&
              source === EXTERNAL_CHANGES_PROTECT_SOURCE.EXTERNAL)
          ) {
            field["status"][source] = EXTERNAL_CHANGES_PROTECT_STATUS.IDLE;
          } else {
            field["status"][source] = status;
          }
        });
      } else {
        this.fieldsArr.forEach(
          field =>
            (field["status"][source] = EXTERNAL_CHANGES_PROTECT_STATUS.IDLE)
        );
      }
    },

    handleCheck(state, name, source, status) {
      const fieldIndex = this.fieldsArr.findIndex(field => field.name === name);
      if (state) {
        this.fieldsArr[fieldIndex]["status"][source] = status;
      } else {
        this.fieldsArr[fieldIndex]["status"][source] =
          EXTERNAL_CHANGES_PROTECT_STATUS.IDLE;
      }
    },

    async handleSave() {
      this.loading.view = true;
      const form = {};
      this.fieldsArr.forEach(field => {
        form[field.name] = field["status"];
      });
      form["company"] = this.company.id;

      try {
        // GTM event data collection below
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.EXTERNAL_CHANGES_PROTECT_EXT_CONFIG_CHANGE,
          num_accept: this.getCheckCount(
            EXTERNAL_CHANGES_PROTECT_SOURCE.EXTERNAL,
            EXTERNAL_CHANGES_PROTECT_STATUS.ACCEPT
          ),
          num_reject: this.getCheckCount(
            EXTERNAL_CHANGES_PROTECT_SOURCE.EXTERNAL,
            EXTERNAL_CHANGES_PROTECT_STATUS.REJECT
          )
        });

        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.EXTERNAL_CHANGES_PROTECT_GMB_CONFIG_CHANGE,
          num_accept: this.getCheckCount(
            EXTERNAL_CHANGES_PROTECT_SOURCE.GMB,
            EXTERNAL_CHANGES_PROTECT_STATUS.ACCEPT
          ),
          num_reject: this.getCheckCount(
            EXTERNAL_CHANGES_PROTECT_SOURCE.GMB,
            EXTERNAL_CHANGES_PROTECT_STATUS.REJECT
          )
        });
        // end GTM event stuff

        const response = await gmbExternalChangesService.updateExternalChangesSettings(
          {
            id: this.id,
            form
          }
        );

        this.$store.dispatch("externalChanges/fetchProtectionSummary");

        if (response.status === 200) {
          this.infoDialogVisible = true;
        }
      } catch (err) {
        ErrorHandler.displayError(err);
      }
      this.loading.view = false;
    },
    getCheckCount(source, status) {
      return this.areAllChecked(source, status)
        ? "all"
        : this.fieldsArr.reduce((acc, cV) => {
            return cV.status[source] === status ? acc + 1 : acc;
          }, 0);
    }
  }
};
</script>

<style lang="scss" scoped>
.external-changes-protect {
  &__card {
    min-height: 650px;
    color: $dark-blue-3;
  }

  &__subtitle {
    margin-bottom: $spacing * 2.75;
  }

  &__description {
    @include font-size(16px);
    font-weight: 500;
    line-height: 22px;
  }

  .col--name {
    flex: 1;
    min-width: 200px;
    line-height: 22px;
    padding-left: $spacing * 4.5 !important;
    padding-right: $spacing * 4.5 !important;
  }

  .col--external {
    display: flex;
    flex: 0 1 240px;
    max-width: 240px;
    min-width: 120px;
  }

  .col--gmb {
    display: flex;
    flex: 0 1 360px;
    max-width: 360px;
    min-width: 150px;
  }

  .fields-table {
    filter: none;
    overflow: hidden;

    &__headings {
      display: flex;
      flex-wrap: nowrap;
      margin: 0 $spacing * -0.5;
      align-items: stretch;

      > .col {
        margin: 0 $spacing * 0.5;
        padding-left: $spacing;
        padding-right: $spacing;
      }

      h4 {
        @include font-size(16px);
        position: relative;
        font-weight: 500;
        line-height: 32px;
        color: $grey-darker;
        margin-bottom: $spacing * 2;

        &::before {
          content: "";
          position: absolute;
          top: 50%;
          left: -16px;
          width: 3px;
          height: 32px;
          transform: translateY(-50%);
          background-color: $lime;
        }
      }
    }

    &__labels {
      display: flex;
      margin-top: auto;

      p {
        display: flex;
        justify-content: center;
        padding: 0 $spacing;
        margin-bottom: 0;
        color: $grey-darker;
        opacity: 0.6;
        @include font-size(12px);

        &:only-child {
          width: 100%;
        }

        &:not(:only-child) {
          width: 50%;
        }
      }
    }
  }

  .field {
    display: flex;
    @include font-size(14px);
    color: $grey-darker;
    flex-wrap: nowrap;
    margin: 0 $spacing * -0.5;
    align-items: stretch;

    > .col {
      margin: 0 $spacing * 0.5;
      background-color: $light-4;
      padding: $spacing * 2 $spacing;
      position: relative;
    }

    &:not(:last-child) > .col:before {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: $light-3;
    }

    &--all {
      font-weight: 600;
    }

    &__checkbox-wrapper {
      padding: 0 $spacing;
      display: flex;
      justify-content: center;
      align-items: center;

      &:only-child {
        width: 100%;
      }

      &:not(:only-child) {
        width: 50%;
      }

      .v-input {
        margin: 0;
        padding-top: 0;

        .v-input__control {
          .v-input__slot {
            margin-bottom: 0;
          }

          .v-messages {
            display: none;
          }
        }

        ::v-deep .v-input--selection-controls__input {
          margin-right: 0;
        }
      }

      .v-input ~ .v-input {
        margin-left: $spacing * 5.5;
      }
    }
  }
}
</style>

<style lang="scss">
.external-changes-protect {
  .field {
    &__checkbox-wrapper {
      .v-input {
        .v-input__control {
          .v-input__slot {
            margin-bottom: 0;
          }
          .v-messages {
            display: none;
          }
        }
      }
    }
  }
}

.info-icon {
  display: inline;
  margin-left: $spacing;
}
</style>
